import { useState, type FormEvent, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError } from '../api/client';
import {
  useConnectMetaToken,
  useDisconnectMetaAccount,
  useLinkMetaAdAccount,
  useLinkMetaPage,
  useMetaAdAccounts,
  useMetaCampaigns,
  useMetaForms,
  useMetaPages,
  useMetaStatus,
  useResyncMetaPage,
  useRevealVerifyToken,
  useSaveMetaConfig,
  useStartMetaOAuth,
  useSyncMetaCampaigns,
  useSyncMetaForms,
  useTestMetaConfig,
  useUnlinkMetaAdAccount,
  useUnlinkMetaPage,
  useUpdateMetaForm,
} from '../api/hooks';
import type { MetaLeadForm, MetaPage, MetaStatus } from '../api/types';
import { EmptyState, ErrorBanner, formatDateTime, relativeTime } from '../components/ui';
import { MetaFormMappingModal } from './MetaFormMappingModal';

/**
 * Meta Lead Ads settings.
 *
 * Structured as the six-step setup the backend reports in `status.steps`, in
 * order, because that order is causal: a page cannot be linked before an
 * account is connected, and a form cannot be mapped before it is synced. The
 * checklist at the top is the page's table of contents.
 */

/** The redirect Meta returns to. Must also be registered on the Meta app. */
function callbackUrl(): string {
  return `${window.location.origin}/settings/integrations/meta/callback`;
}

/* ------------------------------ error display ----------------------------- */

/**
 * Graph failures arrive with Meta's own wording, which is usually the most
 * useful thing on screen. Two codes get an added line, because the message
 * alone does not say what to do about it.
 */
function MetaError({ error }: { error: unknown }) {
  if (!error) return null;

  let guidance: ReactNode = null;
  if (error instanceof ApiError) {
    const code = (error.body.details as { meta_code?: number } | undefined)?.meta_code;
    if (code === 190) {
      guidance = 'The stored access token is no longer valid. Reconnect the Facebook account below.';
    } else if (code === 200 || code === 10 || code === 3) {
      guidance =
        'The Meta app is missing a permission for this call. It has to be granted through App ' +
        'Review before it works for anyone outside the app’s own roles.';
    } else if (error.code === 'meta_unreachable') {
      guidance = 'Meta could not be reached. This is usually temporary — try again.';
    }
  }

  return (
    <>
      <ErrorBanner error={error} />
      {guidance && <p className="hint" style={{ marginTop: -6, marginBottom: 12 }}>{guidance}</p>}
    </>
  );
}

/* -------------------------------- checklist ------------------------------- */

const STEP_LABELS: Array<{ key: keyof MetaStatus['steps']; label: string; hint: string }> = [
  { key: 'app_configured', label: 'Meta app configured', hint: 'App ID and secret saved' },
  { key: 'account_connected', label: 'Facebook account connected', hint: 'A token we can call Meta with' },
  { key: 'page_linked', label: 'Page linked', hint: 'The page whose forms collect leads' },
  { key: 'page_subscribed', label: 'Webhook subscribed', hint: 'Meta will push new leads to us' },
  { key: 'forms_synced', label: 'Lead forms synced', hint: 'Pulled from the page' },
  { key: 'fields_mapped', label: 'Fields mapped', hint: 'Answers land on CRM fields' },
];

function SetupChecklist({ status }: { status: MetaStatus }) {
  const firstIncomplete = STEP_LABELS.find((s) => !status.steps[s.key]);

  return (
    <div className="card">
      <div className="card-head">
        <div>
          <b>Setup</b>
          <p className="sub" style={{ margin: 0 }}>
            {status.ready
              ? `Live — ${status.counts.leads_from_meta} lead${status.counts.leads_from_meta === 1 ? '' : 's'} captured from Meta so far`
              : firstIncomplete
                ? `Next: ${firstIncomplete.label.toLowerCase()}`
                : 'Not configured'}
          </p>
        </div>
      </div>
      <div className="card-body">
        <ol className="meta-steps">
          {STEP_LABELS.map((step) => {
            const done = status.steps[step.key];
            const isNext = !done && firstIncomplete?.key === step.key;
            return (
              <li key={step.key} data-done={done} data-next={isNext || undefined}>
                <span className="meta-step-mark">{done ? '✓' : ''}</span>
                <div>
                  <b>{step.label}</b>
                  <span className="hint">{step.hint}</span>
                </div>
              </li>
            );
          })}
        </ol>

        {status.webhook.last_received && (
          <p className="hint" style={{ marginTop: 14 }}>
            Last webhook delivery {relativeTime(status.webhook.last_received)}
            {status.webhook.failed_last_24h > 0 && (
              <> · <b className="err">{status.webhook.failed_last_24h} failed in the last 24h</b></>
            )}
            {status.webhook.pending > 0 && <> · {status.webhook.pending} pending</>}
          </p>
        )}
      </div>
    </div>
  );
}

/* ------------------------------- app config ------------------------------- */

function CopyRow({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="field">
      <label>{label}</label>
      <div style={{ display: 'flex', gap: 8 }}>
        <input readOnly value={value} className="mono" onFocus={(e) => e.currentTarget.select()} />
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => {
            void navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

function AppConfigCard({ status }: { status: MetaStatus }) {
  const config = status.config;
  const saveConfig = useSaveMetaConfig();
  const testConfig = useTestMetaConfig();
  const revealToken = useRevealVerifyToken();

  const [appId, setAppId] = useState(config.app_id ?? '');
  const [appSecret, setAppSecret] = useState('');
  const [verifyToken, setVerifyToken] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);

  const fieldErrors = error instanceof ApiError ? error.fieldErrors : {};

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSaved(false);
    try {
      await saveConfig.mutateAsync({
        app_id: appId.trim(),
        // Omitted when unchanged: the API keeps the stored secret as long as
        // the App ID is the same one.
        ...(appSecret.trim() ? { app_secret: appSecret.trim() } : {}),
        ...(verifyToken.trim() ? { verify_token: verifyToken.trim() } : {}),
      });
      setAppSecret('');
      setVerifyToken('');
      setSaved(true);
    } catch (err) {
      setError(err);
    }
  }

  return (
    <div className="card">
      <div className="card-head">
        <div>
          <b>Meta app</b>
          <p className="sub" style={{ margin: 0 }}>
            From developers.facebook.com → your app → Settings → Basic
          </p>
        </div>
        {config.configured && (
          <button
            className="btn btn-ghost btn-sm"
            style={{ marginLeft: 'auto' }}
            disabled={testConfig.isPending}
            onClick={async () => {
              setError(null);
              try {
                await testConfig.mutateAsync();
              } catch (err) {
                setError(err);
              }
            }}
          >
            {testConfig.isPending ? 'Testing…' : 'Test connection'}
          </button>
        )}
      </div>

      <div className="card-body">
        <MetaError error={error} />

        {testConfig.data && (
          <div className="banner banner-info">
            Connected to “{testConfig.data.app_name}” (app {testConfig.data.app_id}).
          </div>
        )}
        {saved && !error && <div className="banner banner-info">Saved and verified with Meta.</div>}

        {config.source === 'env' && (
          <div className="banner banner-info">
            These values are currently coming from the server’s <span className="mono">.env</span>.
            Saving here stores them encrypted in the database instead.
          </div>
        )}

        <form onSubmit={submit}>
          <div className="field">
            <label>
              App ID <span className="req">*</span>
            </label>
            <input
              value={appId}
              onChange={(e) => setAppId(e.target.value)}
              placeholder="1234567890123456"
              className="mono"
              required
            />
            {fieldErrors.app_id && <div className="err">{fieldErrors.app_id}</div>}
          </div>

          <div className="field">
            <label>
              App Secret {!config.has_app_secret && <span className="req">*</span>}
            </label>
            <input
              type="password"
              value={appSecret}
              onChange={(e) => setAppSecret(e.target.value)}
              placeholder={config.has_app_secret ? '•••••••• (stored — leave blank to keep)' : '32 hexadecimal characters'}
              className="mono"
              autoComplete="off"
            />
            {fieldErrors.app_secret && <div className="err">{fieldErrors.app_secret}</div>}
          </div>

          <div className="field">
            <label>Webhook verify token</label>
            <input
              value={verifyToken}
              onChange={(e) => setVerifyToken(e.target.value)}
              placeholder={config.has_verify_token ? '•••••••• (stored — leave blank to keep)' : 'Any hard-to-guess string'}
              className="mono"
              autoComplete="off"
            />
            <span className="hint">
              You choose this value and paste the same one into Meta. It is what stops anyone else
              subscribing to our webhook.
            </span>
          </div>

          <button className="btn btn-primary" disabled={saveConfig.isPending || !appId.trim()}>
            {saveConfig.isPending ? 'Verifying with Meta…' : 'Save app credentials'}
          </button>
        </form>

        <hr style={{ margin: '20px 0', border: 0, borderTop: '1px solid var(--line, #e5e7eb)' }} />

        <p className="hint" style={{ marginTop: 0 }}>
          Paste these two into the Meta app under Webhooks → Page → leadgen.
        </p>

        {config.webhook_url ? (
          <CopyRow label="Callback URL" value={config.webhook_url} />
        ) : (
          <div className="banner banner-error">
            The server does not know its public URL, so the callback URL cannot be shown. Set
            <span className="mono"> PUBLIC_BASE_URL</span> in the backend’s environment. Meta also
            requires HTTPS with a valid certificate — it will not call plain HTTP.
          </div>
        )}

        {revealToken.data ? (
          <CopyRow label="Verify token" value={revealToken.data} />
        ) : (
          <button
            className="btn btn-ghost btn-sm"
            disabled={!config.has_verify_token || revealToken.isPending}
            onClick={() => revealToken.mutate()}
          >
            {config.has_verify_token ? 'Reveal verify token' : 'No verify token saved yet'}
          </button>
        )}
      </div>
    </div>
  );
}

/* --------------------------------- account -------------------------------- */

function AccountCard({ status }: { status: MetaStatus }) {
  const account = status.account;
  const startOAuth = useStartMetaOAuth();
  const connectToken = useConnectMetaToken();
  const disconnect = useDisconnectMetaAccount();

  const [showManual, setShowManual] = useState(false);
  const [token, setToken] = useState('');
  const [error, setError] = useState<unknown>(null);

  const canConnect = status.steps.app_configured;

  async function connectWithFacebook() {
    setError(null);
    try {
      const { url } = await startOAuth.mutateAsync(callbackUrl());
      // A full-page navigation rather than a popup: popups are blocked often
      // enough that the flow would fail for some admins with no explanation.
      window.location.assign(url);
    } catch (err) {
      setError(err);
    }
  }

  async function connectWithToken(event: FormEvent) {
    event.preventDefault();
    setError(null);
    try {
      await connectToken.mutateAsync(token.trim());
      setToken('');
      setShowManual(false);
    } catch (err) {
      setError(err);
    }
  }

  return (
    <div className="card">
      <div className="card-head">
        <div>
          <b>Facebook account</b>
          <p className="sub" style={{ margin: 0 }}>
            The account whose pages and ad accounts this CRM may read
          </p>
        </div>
        {account.connected && (
          <button
            className="btn btn-ghost btn-sm"
            style={{ marginLeft: 'auto' }}
            disabled={disconnect.isPending}
            onClick={async () => {
              if (!confirm('Disconnect this Facebook account? Linked pages keep working — their page tokens are stored separately — but you will not be able to link new pages or sync campaigns until you reconnect.')) return;
              setError(null);
              try {
                await disconnect.mutateAsync();
              } catch (err) {
                setError(err);
              }
            }}
          >
            Disconnect
          </button>
        )}
      </div>

      <div className="card-body">
        <MetaError error={error} />

        {!canConnect && (
          <div className="banner banner-info">Save the Meta app credentials above first.</div>
        )}

        {account.connected ? (
          <>
            <div className="kv">
              <span>Account</span>
              <b>{account.name ?? 'Unknown'}</b>
            </div>
            <div className="kv">
              <span>User ID</span>
              <span className="mono">{account.user_id}</span>
            </div>
            <div className="kv">
              <span>Access expires</span>
              <span>{account.expires_at ? formatDateTime(account.expires_at) : 'Does not expire'}</span>
            </div>
            <div className="kv">
              <span>Granted</span>
              <span>
                {account.scopes.length > 0
                  ? account.scopes.map((s) => (
                      <span className="tag" key={s} style={{ marginRight: 4 }}>{s}</span>
                    ))
                  : '—'}
              </span>
            </div>

            {account.missing_scopes.length > 0 && (
              <div className="banner banner-error" style={{ marginTop: 12 }}>
                <div>
                  <b>Missing permissions:</b>{' '}
                  {account.missing_scopes.join(', ')}
                  <div className="hint" style={{ marginTop: 4 }}>
                    Meta grants these only after App Review. Without{' '}
                    <span className="mono">leads_retrieval</span> in particular, lead answers cannot
                    be read and every delivered lead will be skipped.
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <button
              className="btn btn-primary"
              disabled={!canConnect || startOAuth.isPending}
              onClick={connectWithFacebook}
            >
              {startOAuth.isPending ? 'Opening Facebook…' : 'Connect with Facebook'}
            </button>
            <p className="hint" style={{ marginTop: 10 }}>
              You will be sent to Facebook and returned here. The redirect URL{' '}
              <span className="mono">{callbackUrl()}</span> must be listed under Valid OAuth Redirect
              URIs in the Meta app.
            </p>
          </>
        )}

        <hr style={{ margin: '18px 0', border: 0, borderTop: '1px solid var(--line, #e5e7eb)' }} />

        <button className="btn btn-ghost btn-sm" onClick={() => setShowManual((v) => !v)}>
          {showManual ? 'Hide' : 'Advanced: paste an access token'}
        </button>

        {showManual && (
          <form onSubmit={connectWithToken} style={{ marginTop: 12 }}>
            <p className="hint" style={{ marginTop: 0 }}>
              Facebook Login cannot grant <span className="mono">leads_retrieval</span> or{' '}
              <span className="mono">pages_show_list</span> until Meta completes App Review, so
              before then the only way to test is a user token from the Graph API Explorer,
              generated by someone with a role on the app. It is exchanged for a long-lived token
              exactly like the login flow.
            </p>
            <div className="field">
              <label>User access token</label>
              <textarea
                value={token}
                onChange={(e) => setToken(e.target.value)}
                rows={3}
                className="mono"
                placeholder="EAAG…"
                required
              />
            </div>
            <button className="btn btn-primary" disabled={connectToken.isPending || token.trim().length < 50}>
              {connectToken.isPending ? 'Verifying with Meta…' : 'Connect with this token'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

/* ---------------------------------- pages --------------------------------- */

function FormRow({ form, onEdit }: { form: MetaLeadForm; onEdit: () => void }) {
  const updateForm = useUpdateMetaForm();
  const unmapped = form.unmapped_questions.length;

  return (
    <tr>
      <td>
        <b>{form.name}</b>
        <div className="mono hint">{form.external_id}</div>
      </td>
      <td className="num">{form.lead_count}</td>
      <td>
        {unmapped === 0 ? (
          <span className="chip chip-plain">all mapped</span>
        ) : (
          <span className="chip" title="These answers are not written to any CRM field">
            {unmapped} unmapped
          </span>
        )}
      </td>
      <td>
        <label className="switch">
          <input
            type="checkbox"
            checked={form.is_active}
            disabled={updateForm.isPending}
            onChange={(e) => updateForm.mutate({ id: form.id, is_active: e.target.checked })}
          />
          <span className="track" />
        </label>
      </td>
      <td style={{ textAlign: 'right' }}>
        <button className="btn btn-ghost btn-sm" onClick={onEdit}>
          Edit mapping
        </button>
      </td>
    </tr>
  );
}

function PageCard({ page, onError }: { page: MetaPage; onError: (err: unknown) => void }) {
  const { data: forms = [], isPending } = useMetaForms(page.id);
  const syncForms = useSyncMetaForms();
  const resync = useResyncMetaPage();
  const unlink = useUnlinkMetaPage();
  const [editing, setEditing] = useState<string | null>(null);
  const [syncResult, setSyncResult] = useState<string | null>(null);

  async function guard(run: () => Promise<unknown>) {
    onError(null);
    try {
      await run();
    } catch (err) {
      onError(err);
    }
  }

  return (
    <div className="card" style={{ marginTop: 12 }}>
      <div className="card-head">
        <div>
          <b>{page.name}</b>
          <p className="sub" style={{ margin: 0 }}>
            <span className="mono">{page.page_id}</span>
            {' · '}
            {page.subscribed ? (
              <span className="chip chip-plain">webhook subscribed</span>
            ) : (
              <span className="chip">not subscribed</span>
            )}
            {page.last_synced_at && ` · synced ${relativeTime(page.last_synced_at)}`}
          </p>
        </div>
        <div className="page-head-actions" style={{ marginLeft: 'auto' }}>
          <button
            className="btn btn-ghost btn-sm"
            disabled={syncForms.isPending}
            onClick={() =>
              guard(async () => {
                const result = await syncForms.mutateAsync(page.id);
                setSyncResult(
                  `${result.total} form${result.total === 1 ? '' : 's'} found — ${result.created} new, ${result.updated} updated`,
                );
              })
            }
          >
            {syncForms.isPending ? 'Syncing…' : 'Sync forms'}
          </button>
          <button
            className="btn btn-ghost btn-sm"
            disabled={resync.isPending}
            title="Refresh the page token and re-assert the leadgen subscription"
            onClick={() => guard(() => resync.mutateAsync(page.id))}
          >
            {resync.isPending ? 'Retrying…' : 'Retry subscription'}
          </button>
          <button
            className="btn btn-ghost btn-sm btn-danger"
            disabled={unlink.isPending}
            onClick={() => {
              if (!confirm(`Unlink “${page.name}”? Meta will stop sending its leads, and its ${page.form_count} form(s) will be switched off.`)) return;
              void guard(() => unlink.mutateAsync(page.id));
            }}
          >
            Unlink
          </button>
        </div>
      </div>

      <div className="card-body">
        {page.last_error && (
          <div className="banner banner-error">
            <div>
              <b>Last operation failed:</b> {page.last_error}
              <div className="hint" style={{ marginTop: 4 }}>
                Leads will not arrive until this is resolved. Fix the cause, then press Retry
                subscription.
              </div>
            </div>
          </div>
        )}
        {syncResult && <div className="banner banner-info">{syncResult}</div>}

        {isPending && <div className="hint">Loading forms…</div>}

        {!isPending && forms.length === 0 && (
          <EmptyState title="No lead forms yet">
            Press “Sync forms” to pull this page’s forms from Meta. A lead whose form is not stored
            here is dropped on arrival, so this step is not optional.
          </EmptyState>
        )}

        {forms.length > 0 && (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Form</th>
                  <th className="num">Leads</th>
                  <th>Mapping</th>
                  <th>Active</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {forms.map((form) => (
                  <FormRow key={form.id} form={form} onEdit={() => setEditing(form.id)} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && <MetaFormMappingModal formId={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function PagesSection({ status, onError }: { status: MetaStatus; onError: (err: unknown) => void }) {
  const { data, isPending } = useMetaPages();
  const linkPage = useLinkMetaPage();
  // Which page is being linked, so one row's progress does not relabel them all.
  const [linking, setLinking] = useState<string | null>(null);

  if (!status.steps.account_connected && (data?.linked.length ?? 0) === 0) return null;

  const available = data?.available.filter((p) => !p.linked) ?? [];

  return (
    <>
      <div className="card">
        <div className="card-head">
          <div>
            <b>Pages</b>
            <p className="sub" style={{ margin: 0 }}>
              Linking a page subscribes this CRM to its lead notifications
            </p>
          </div>
        </div>
        <div className="card-body">
          {data?.available_error && (
            <div className="banner banner-error">
              <div>
                <b>Could not list pages from Meta:</b> {data.available_error}
                <div className="hint" style={{ marginTop: 4 }}>
                  Pages already linked are unaffected and remain manageable below.
                </div>
              </div>
            </div>
          )}

          {isPending && <div className="hint">Loading pages…</div>}

          {available.length > 0 && (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Available page</th>
                    <th>Category</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {available.map((page) => (
                    <tr key={page.page_id}>
                      <td>
                        <b>{page.name}</b>
                        <div className="mono hint">{page.page_id}</div>
                      </td>
                      <td>{page.category ?? '—'}</td>
                      <td style={{ textAlign: 'right' }}>
                        <button
                          className="btn btn-primary btn-sm"
                          disabled={linkPage.isPending}
                          onClick={async () => {
                            onError(null);
                            setLinking(page.page_id);
                            try {
                              await linkPage.mutateAsync(page.page_id);
                            } catch (err) {
                              onError(err);
                            } finally {
                              setLinking(null);
                            }
                          }}
                        >
                          {linking === page.page_id ? 'Linking…' : 'Link'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!isPending && available.length === 0 && (data?.linked.length ?? 0) === 0 && (
            <EmptyState title="No pages available">
              This Facebook account does not administer any page the app can see. It needs a page
              admin role, and the app needs the <span className="mono">pages_show_list</span>{' '}
              permission.
            </EmptyState>
          )}
        </div>
      </div>

      {data?.linked.map((page) => <PageCard key={page.id} page={page} onError={onError} />)}
    </>
  );
}

/* --------------------------- ad accounts & campaigns ---------------------- */

function CampaignsSection({ status, onError }: { status: MetaStatus; onError: (err: unknown) => void }) {
  const { data: accounts } = useMetaAdAccounts();
  const linkAccount = useLinkMetaAdAccount();
  const unlinkAccount = useUnlinkMetaAdAccount();
  const syncCampaigns = useSyncMetaCampaigns();
  const [linkingAccount, setLinkingAccount] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const { data: campaigns = [], isPending } = useMetaCampaigns({ search });

  if (!status.steps.account_connected && status.counts.campaigns === 0) return null;

  const available = accounts?.available.filter((a) => !a.linked) ?? [];

  async function guard(run: () => Promise<unknown>) {
    onError(null);
    try {
      await run();
    } catch (err) {
      onError(err);
    }
  }

  return (
    <div className="card">
      <div className="card-head">
        <div>
          <b>Campaigns</b>
          <p className="sub" style={{ margin: 0 }}>
            Pulled from the ad account, and joined to the leads each one produced
          </p>
        </div>
        <div className="page-head-actions" style={{ marginLeft: 'auto' }}>
          <button
            className="btn btn-ghost btn-sm"
            disabled={syncCampaigns.isPending || (accounts?.linked.length ?? 0) === 0}
            onClick={() => guard(() => syncCampaigns.mutateAsync(undefined))}
          >
            {syncCampaigns.isPending ? 'Syncing…' : 'Sync campaigns'}
          </button>
        </div>
      </div>

      <div className="card-body">
        {accounts?.available_error && (
          <div className="banner banner-error">
            Could not list ad accounts: {accounts.available_error}
          </div>
        )}

        {(accounts?.linked.length ?? 0) > 0 && (
          <div style={{ marginBottom: 12 }}>
            {accounts?.linked.map((account) => (
              <div className="kv" key={account.id}>
                <span>
                  {account.name} <span className="mono hint">{account.external_id}</span>
                </span>
                <span>
                  {account.campaign_count} campaign{account.campaign_count === 1 ? '' : 's'}
                  {' · '}
                  <button
                    className="btn btn-ghost btn-sm"
                    disabled={unlinkAccount.isPending}
                    onClick={() => {
                      if (!confirm(`Unlink ${account.name}? Campaigns already synced are kept, so leads attributed to them keep resolving.`)) return;
                      void guard(() => unlinkAccount.mutateAsync(account.id));
                    }}
                  >
                    Unlink
                  </button>
                </span>
              </div>
            ))}
          </div>
        )}

        {available.length > 0 && (
          <div className="field">
            <label>Link an ad account</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {available.map((account) => (
                <button
                  key={account.external_id}
                  className="btn btn-ghost btn-sm"
                  disabled={linkAccount.isPending}
                  onClick={async () => {
                    setLinkingAccount(account.external_id);
                    await guard(() => linkAccount.mutateAsync(account.external_id));
                    setLinkingAccount(null);
                  }}
                >
                  {linkingAccount === account.external_id ? 'Linking…' : `+ ${account.name}`}
                  {account.currency && linkingAccount !== account.external_id && (
                    <span className="hint"> ({account.currency})</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {status.counts.campaigns === 0 ? (
          <EmptyState title="No campaigns yet">
            Link an ad account and sync, or wait for the first lead — a campaign is also created
            automatically the first time a lead arrives from it.
          </EmptyState>
        ) : (
          <>
            <div className="filters" style={{ marginBottom: 10 }}>
              <input
                placeholder="Search campaigns…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Campaign</th>
                    <th>Status</th>
                    <th>Objective</th>
                    <th className="num">Ad sets</th>
                    <th className="num">Leads</th>
                    <th className="num">Daily budget</th>
                  </tr>
                </thead>
                <tbody>
                  {isPending && (
                    <tr>
                      <td colSpan={6} className="hint">Loading…</td>
                    </tr>
                  )}
                  {campaigns.map((campaign) => (
                    <tr key={campaign.id}>
                      <td>
                        <b>{campaign.name}</b>
                        <div className="mono hint">{campaign.external_id}</div>
                        {campaign.discovered_from_lead && (
                          <span className="chip" title="Created from an incoming lead; never synced from the Marketing API">
                            not synced
                          </span>
                        )}
                      </td>
                      <td>{campaign.status ?? '—'}</td>
                      <td className="truncate">{campaign.objective ?? '—'}</td>
                      <td className="num">{campaign.ad_set_count}</td>
                      <td className="num">
                        <b>{campaign.lead_count}</b>
                      </td>
                      <td className="num">
                        {campaign.daily_budget === null ? '—' : campaign.daily_budget.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ---------------------------------- page ---------------------------------- */

export function MetaIntegrationPage() {
  const qc = useQueryClient();
  const [error, setError] = useState<unknown>(null);
  const { data: status, isPending, error: statusError } = useMetaStatus();

  if (isPending) {
    return (
      <div className="page">
        <div style={{ display: 'grid', placeItems: 'center', minHeight: 240 }}>
          <span className="spinner" />
        </div>
      </div>
    );
  }

  if (!status) {
    return (
      <div className="page">
        <ErrorBanner error={statusError} />
      </div>
    );
  }

  const handleError = (err: unknown) => {
    setError(err);
    // A failed mutation skips its own onSuccess, so nothing would refetch — yet
    // a half-completed operation is exactly the case where the screen is most
    // out of date. Linking a page, for instance, stores the page and *then*
    // subscribes it, so a subscription failure still changed server state.
    void qc.invalidateQueries({ queryKey: ['meta'] });
  };

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Meta Lead Ads</h1>
          <p className="sub">
            Connect Facebook so leads from your lead-ad forms arrive here automatically, attributed
            to the campaign that produced them
          </p>
        </div>
      </div>

      <MetaError error={error} />

      <SetupChecklist status={status} />
      <AppConfigCard status={status} />
      <AccountCard status={status} />
      <PagesSection status={status} onError={handleError} />
      <CampaignsSection status={status} onError={handleError} />
    </div>
  );
}
